import React, { useEffect, useState } from 'react';
import { MeetingContext, isClientContext } from '../../types/booking';

interface WaitingRoomProps {
  context: MeetingContext | null;
  onReady: () => void;
}

function formatCountdown(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

const WaitingRoom: React.FC<WaitingRoomProps> = ({ context, onReady }) => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(context?.state === 'ready');

  useEffect(() => {
    if (!context) return;

    if (context.state === 'ready') {
      setIsReady(true);
      return;
    }

    if (!isClientContext(context) || !context.appointment_at) return;

    const apptMs = new Date(context.appointment_at).getTime();
    const readyAtMs = apptMs - 15 * 60 * 1000;

    let id: ReturnType<typeof setInterval>;
    const tick = () => {
      const diff = Math.floor((readyAtMs - Date.now()) / 1000);
      if (diff <= 0) {
        setIsReady(true);
        setSecondsLeft(0);
        clearInterval(id);
      } else {
        setSecondsLeft(diff);
      }
    };

    tick();
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [context]);

  const name = context
    ? isClientContext(context)
      ? context.client_name ?? 'Client'
      : context.guest_name ?? 'Guest'
    : null;

  const roomName = context?.room_name ?? null;
  const agenda = context && isClientContext(context) ? context.agenda : null;

  return (
    <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center p-6">
      {/* Logo / brand header */}
      <div className="mb-10 text-center">
        <h1 className="text-2xl font-semibold text-white tracking-tight">
          my investments
        </h1>
        <p className="text-slate-400 text-sm mt-1">Virtual Branch</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-slate-900 border border-slate-700/60 rounded-2xl p-8 shadow-2xl text-center">
        {/* Pulse indicator */}
        <div className="flex justify-center mb-6">
          <span className="relative flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-4 w-4 bg-teal-500" />
          </span>
        </div>

        {name && (
          <p className="text-slate-400 text-sm mb-1">Welcome, {name}</p>
        )}
        <h2 className="text-white text-xl font-semibold mb-1">
          Your meeting starts soon
        </h2>
        {roomName && (
          <p className="text-slate-500 text-sm mb-6">{roomName}</p>
        )}

        {/* Countdown */}
        {!isReady && secondsLeft !== null && (
          <div className="mb-6">
            <p className="text-slate-500 text-xs uppercase tracking-widest mb-2">
              Time until entry opens
            </p>
            <p
              data-testid="countdown"
              className="text-4xl font-mono font-bold text-teal-400 tabular-nums"
            >
              {formatCountdown(secondsLeft)}
            </p>
          </div>
        )}

        {/* Advisor preparing teaser */}
        <p className="text-slate-400 text-sm mb-6 italic">
          Your advisor is already preparing for your meeting.
        </p>

        {/* Agenda recap */}
        {agenda && (
          <div className="bg-slate-800/60 rounded-lg p-4 mb-6 text-left">
            <p className="text-slate-500 text-xs uppercase tracking-widest mb-2">
              Meeting Agenda
            </p>
            <p className="text-slate-300 text-sm leading-relaxed line-clamp-4">
              {agenda}
            </p>
          </div>
        )}

        {/* Enter Branch CTA */}
        <button
          data-testid="enter-branch-btn"
          onClick={() => isReady && onReady()}
          disabled={!isReady}
          className={
            isReady
              ? 'w-full py-3 px-6 rounded-lg font-semibold text-white bg-teal-600 hover:bg-teal-500 transition-colors cursor-pointer'
              : 'w-full py-3 px-6 rounded-lg font-semibold text-slate-500 bg-slate-800 cursor-not-allowed'
          }
        >
          Enter Branch
        </button>

        {!isReady && (
          <p className="text-slate-600 text-xs mt-3">
            Entry opens 15 minutes before your appointment
          </p>
        )}
      </div>
    </div>
  );
};

export default WaitingRoom;
